import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Home as HomeIcon, Heart, Shield, TrendingUp, Award, Users, MapPin, Phone, Mail } from 'lucide-react';
import { getCompanyInfo, getTeamMembers, getSiteSettings } from '../services/api';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const About = () => {
  const [companyInfo, setCompanyInfo] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [info, members, settings] = await Promise.all([
          getCompanyInfo(),
          getTeamMembers(),
          getSiteSettings()
        ]);
        setCompanyInfo(info);
        setTeamMembers(members);
        setSiteSettings(settings);
      } catch (error) {
        console.error('Failed to load about data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <p className="text-xl text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO 
        title="About Us"
        description="Learn about Otter River Properties - your trusted property management partner in Lynchburg, VA. Discover our mission, values, and commitment to exceptional service."
        keywords="about Otter River Properties, property management company Lynchburg, rental management services, Lynchburg VA property managers"
      />
      
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <img 
          src={siteSettings?.about_hero_image || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1600&h=600&fit=crop'}
          alt="About Otter River Properties"
          className="absolute inset-0 w-full h-full"
          style={{ objectFit: siteSettings?.about_hero_image_fit || 'cover' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/50"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-5xl font-bold text-white mb-4">About Otter River Properties</h1>
          <p className="text-xl text-gray-100">Building communities and creating homes since 2008</p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-white dark:bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Our Story</h2>
              <div 
                className="text-lg text-gray-600 dark:text-gray-300 space-y-4"
                dangerouslySetInnerHTML={{
                  __html: companyInfo?.about || "<p>Otter River Properties is a family-owned and operated business that has been serving the Lynchburg community for over 15 years.</p>"
                }}
              />
              <div 
                className="mt-6 text-lg text-gray-800 dark:text-gray-200 font-medium space-y-4"
                dangerouslySetInnerHTML={{
                  __html: companyInfo?.mission || "<p>As a family business, our mission is to provide superior property management services.</p>"
                }}
              />
            </div>
            <div className="h-96 overflow-hidden rounded-lg shadow-2xl">
              <img
                src={siteSettings?.about_story_image || "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800&h=600&fit=crop"}
                alt="Beautiful home managed by Otter River Properties"
                className="w-full h-full"
                style={{ objectFit: siteSettings?.about_story_image_fit || 'cover' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white text-center mb-4">Our Core Values</h2>
          <p className="text-gray-600 dark:text-gray-400 text-center mb-12">The principles that guide everything we do</p>
          
          <div className="grid md:grid-cols-4 gap-8">
            <Card className="text-center dark:bg-gray-800 dark:border-gray-700 hover:shadow-xl transition-shadow">
              <CardContent className="pt-8">
                <div className="inline-block p-4 bg-otter-green/10 dark:bg-otter-green/20 rounded-full mb-4">
                  <Award className="h-10 w-10 text-otter-green dark:text-otter-green-400" />
                </div>
                <h3 className="text-xl font-bold mb-3 dark:text-white">Excellence</h3>
                <p className="text-gray-600 dark:text-gray-400">We strive for excellence in every property and every interaction</p>
              </CardContent>
            </Card>
            
            <Card className="text-center dark:bg-gray-800 dark:border-gray-700 hover:shadow-xl transition-shadow">
              <CardContent className="pt-8">
                <div className="inline-block p-4 bg-otter-green/10 dark:bg-otter-green/20 rounded-full mb-4">
                  <Heart className="h-10 w-10 text-otter-green dark:text-otter-green-400" />
                </div>
                <h3 className="text-xl font-bold mb-3 dark:text-white">Integrity</h3>
                <p className="text-gray-600 dark:text-gray-400">Honest, transparent service you can trust and depend on</p>
              </CardContent>
            </Card>
            
            <Card className="text-center dark:bg-gray-800 dark:border-gray-700 hover:shadow-xl transition-shadow">
              <CardContent className="pt-8">
                <div className="inline-block p-4 bg-otter-green/10 dark:bg-otter-green/20 rounded-full mb-4">
                  <Users className="h-10 w-10 text-otter-green dark:text-otter-green-400" />
                </div>
                <h3 className="text-xl font-bold mb-3 dark:text-white">Commitment</h3>
                <p className="text-gray-600 dark:text-gray-400">Dedicated service for property owners and tenants alike</p>
              </CardContent>
            </Card>
            
            <Card className="text-center dark:bg-gray-800 dark:border-gray-700 hover:shadow-xl transition-shadow">
              <CardContent className="pt-8">
                <div className="inline-block p-4 bg-otter-green/10 dark:bg-otter-green/20 rounded-full mb-4">
                  <Shield className="h-10 w-10 text-otter-green dark:text-otter-green-400" />
                </div>
                <h3 className="text-xl font-bold mb-3 dark:text-white">Reliability</h3>
                <p className="text-gray-600 dark:text-gray-400">Consistent, dependable service you can count on</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-otter-green dark:bg-otter-green-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <div className="text-5xl font-bold mb-2">15+</div>
              <div className="text-lg opacity-90">Years of Service</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">500+</div>
              <div className="text-lg opacity-90">Properties Managed</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">1000+</div>
              <div className="text-lg opacity-90">Happy Tenants</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">98%</div>
              <div className="text-lg opacity-90">Satisfaction Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      {teamMembers.length > 0 && (
        <section className="py-20 bg-white dark:bg-gray-950">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white text-center mb-4">Meet Our Team</h2>
            <p className="text-gray-600 dark:text-gray-400 text-center mb-12">Experienced professionals dedicated to your success</p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {teamMembers.map((member) => (
                <Card key={member.id} className="text-center dark:bg-gray-800 dark:border-gray-700 hover:shadow-xl transition-shadow">
                  <CardContent className="pt-8">
                    {member.photo ? (
                      <img
                        src={member.photo}
                        alt={member.name}
                        className="w-32 h-32 rounded-full mx-auto mb-4 object-cover border-4 border-otter-green/20"
                      />
                    ) : (
                      <div className="w-32 h-32 rounded-full mx-auto mb-4 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                        <Users className="h-16 w-16 text-gray-400" />
                      </div>
                    )}
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{member.name}</h3>
                    <p className="text-otter-green dark:text-otter-green-400 font-medium mb-3">{member.role}</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">{member.bio}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Location Banner */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <img 
          src={siteSettings?.about_team_image || 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=1600&h=600&fit=crop'}
          alt="Location"
          className="absolute inset-0 w-full h-full"
          style={{ objectFit: siteSettings?.about_team_image_fit || 'cover' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/60"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl font-bold text-white mb-4">{siteSettings?.about_location_title || "Serving Lynchburg with Pride"}</h2>
          <p className="text-xl text-gray-100">{siteSettings?.about_location_description || "Our local team knows the Lynchburg area inside and out, ensuring the best service for property owners and tenants alike"}</p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">Whether you're looking for a new home or need property management services, we're here to help</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/listings">
              <Button className="bg-otter-green hover:bg-otter-green-600 text-white px-8 py-6 text-lg">
                View Properties
              </Button>
            </Link>
            <Link to="/qualifications">
              <Button variant="outline" className="border-otter-green text-otter-green hover:bg-otter-green hover:text-white dark:border-otter-green-400 dark:text-otter-green-400 px-8 py-6 text-lg">
                Tenant Info
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
